/// <amd-module name="Repos/Views/PullRequest/Discussion/PullRequestDiscussionFilterService" />
import { GitPullRequestCommentThread } from "Repos/Common/Contracts/Git";
import { DiscussionStatus, IDiscussionCommentModel, IDiscussionThreadModel } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IObservableValue, IReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";
import { IListBoxItem } from "VSSUI/ListBox";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { renderFilterIdentityRowWithCount, renderFilterRowWithCount } from "../Util/Filter";

export interface IGitPullRequestCommentThreadModel extends IDiscussionThreadModel<GitPullRequestCommentThread> { }

export namespace DiscussionFilterKeys {
    export const commentPreference = "commentPreference";
    export const commentStatus = "commentStatus";
    export const commentOwner = "commentOwner";
}

export const enum DiscussionFilterCommentedKeys {
    all = "file-comment-all",
    commented = "file-comment-commented"
}

export const enum DiscussionFilterCommentPreferenceKeys {
    show = "comments-show",
    new = "comments-new",
    hide = "comments-hide"
}

export const enum DiscussionFilterCommentStatusKeys {
    all = "comment-status-all",
    active = "comment-status-active",
    pending = "comment-status-pending",
    resolved = "comment-status-resolved",
    wontFix = "comment-status-wont-fix",
    byDesign = "comment-status-by-design",
    closed = "comment-status-closed"
}

export const enum DiscussionFilterCommentOwnerKeys {
    all = "comment-owner-all",
    my = "comment-owner-my"
}

const defaultThreadFilter = (t: IGitPullRequestCommentThreadModel) => t.thread.threadContext && !!t.thread.threadContext.filePath;
const defaultCommentFilter = (c: IDiscussionCommentModel) => c.isSaved() && !c.isDeleted().value;
const setCount = (count: IObservableValue<number>, newCount: number): void => {
    if (count.value !== newCount) {
        count.value = newCount;
    }
};

export interface IPullRequestDiscussionFilterService extends IVssService {
    applyFilter(filterState: IFilterState): void;
    applyFilterOnThreads(discussionThreads: IGitPullRequestCommentThreadModel[], visible?: boolean, expanded?: boolean): void;
    fileContainsThread(filePath: string): boolean;
    getCommentPreferenceOptions(): IReadonlyObservableArray<IListBoxItem<string>>;
    getCommentStatusOptions(): IReadonlyObservableArray<IListBoxItem<string>>;
    getCommentOwnerOptions(): IReadonlyObservableArray<IListBoxItem<IdentityRef>>;
    getFileTypeOptions(): IReadonlyObservableArray<IListBoxItem<string>>;
    getFilteredThreads(): IReadonlyObservableArray<IDiscussionThreadModel<GitPullRequestCommentThread>>;
    setFilterState(filterState: IFilterState, selectedThreadId?: number): void;
    updateFilePaths(filePaths: string[]): void;
    updateDiscussionThreads(discussionThreads: IGitPullRequestCommentThreadModel[]): void;
}

class PullRequestDiscussionFilterService extends VssService implements IPullRequestDiscussionFilterService {
    private currentUser: IdentityRef;
    private fileTypeFilterOptions = new ObservableArray<IListBoxItem<string>>();
    private selectedThreadId: number | undefined;

    private commentOwner: string | IdentityRef | null = null;
    private commentOwnerMap: { [key: string]: { count: IObservableValue<number>; identity?: IdentityRef; text: string } } = {};
    private commentOwnerFilterOptions = new ObservableArray<IListBoxItem<IdentityRef>>();
    private commentOwnerCounts: { [key: string]: IObservableValue<number> } = {};

    private commentPreference: string | null = null;
    private commentPreferenceFilterOptions = new ObservableArray<IListBoxItem<string>>();
    private visibleCommentCount = new ObservableValue(0);
    private newCommentCount = new ObservableValue(0);
    private hiddenCommentCount = new ObservableValue(0);

    private commentStatus: string[] | null = null;
    private commentStatusMap: { [key: string]: { count: IObservableValue<number>; status: DiscussionStatus; text: string } } = {};
    private commentStatusFilterOptions = new ObservableArray<IListBoxItem<string>>();

    private filePaths: string[] | null = null;
    private discussionThreadsFiltered = false;
    private discussionThreads?: IGitPullRequestCommentThreadModel[];
    private filteredThreads = new ObservableArray<IGitPullRequestCommentThreadModel>();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.fileTypeFilterOptions.value = [
            { id: DiscussionFilterCommentedKeys.all, text: Resources.CommentedFilterAll },
            { id: DiscussionFilterCommentedKeys.commented, text: Resources.CommentedFilterCommentedFiles }
        ];

        this.commentPreferenceFilterOptions.value = [
            {
                id: DiscussionFilterCommentPreferenceKeys.show,
                render: (ri, ci, tc, ti) => renderFilterRowWithCount(this.visibleCommentCount, ri, ci, tc, ti),
                text: Resources.DiscussionFilterCommentPreferenceShow
            },
            {
                id: DiscussionFilterCommentPreferenceKeys.new,
                render: (ri, ci, tc, ti) => renderFilterRowWithCount(this.newCommentCount, ri, ci, tc, ti),
                text: Resources.DiscussionFilterCommentPreferenceWhatsNew
            },
            {
                id: DiscussionFilterCommentPreferenceKeys.hide,
                render: (ri, ci, tc, ti) => renderFilterRowWithCount(this.hiddenCommentCount, ri, ci, tc, ti),
                text: Resources.DiscussionFilterCommentPreferenceHide
            }
        ];

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.active] = {
            status: DiscussionStatus.Active,
            text: Resources.CommentStatusFilterActiveComments,
            count: new ObservableValue(0)
        };

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.pending] = {
            status: DiscussionStatus.Pending,
            text: Resources.CommentStatusFilterPendingComments,
            count: new ObservableValue(0)
        };

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.resolved] = {
            status: DiscussionStatus.Fixed,
            text: Resources.CommentStatusFilterResolvedComments,
            count: new ObservableValue(0)
        };

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.byDesign] = {
            status: DiscussionStatus.ByDesign,
            text: Resources.CommentStatusFilterByDesignComments,
            count: new ObservableValue(0)
        };

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.wontFix] = {
            status: DiscussionStatus.WontFix,
            text: Resources.CommentStatusFilterWontFixComments,
            count: new ObservableValue(0)
        };

        this.commentStatusMap[DiscussionFilterCommentStatusKeys.closed] = {
            status: DiscussionStatus.Closed,
            text: Resources.CommentStatusFilterClosedComments,
            count: new ObservableValue(0)
        };

        this.commentStatusFilterOptions.value = Object.keys(this.commentStatusMap).map(key => {
            const statusItem = this.commentStatusMap[key];
            return {
                id: key,
                render: (ri, ci, tc, ti) => renderFilterRowWithCount(statusItem.count, ri, ci, tc, ti),
                text: statusItem.text
            };
        });

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        this.currentUser = pageData.user;

        this.commentOwnerMap[DiscussionFilterCommentOwnerKeys.all] = {
            count: new ObservableValue(0),
            text: Resources.CommentOwnerFilterAllComments
        };

        this.commentOwnerMap[this.currentUser.id] = {
            count: new ObservableValue(0),
            text: Resources.CommentOwnerFilterMyComments,
            identity: this.currentUser
        };

        this.commentOwnerFilterOptions.value = Object.keys(this.commentOwnerMap).map(key => {
            const ownerItem = this.commentOwnerMap[key];
            return {
                data: ownerItem.identity,
                id: key,
                render: (ri, ci, tc, ti) => renderFilterIdentityRowWithCount(ownerItem.count, ri, ci, tc, ti),
                text: ownerItem.text
            };
        });
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        delete this.discussionThreads;
    }

    public fileContainsThread(filePath: string): boolean {
        if (this.discussionThreads) {
            return this.discussionThreads.some(t => t.thread.threadContext && t.thread.threadContext.filePath === filePath);
        }

        return false;
    }

    public getCommentOwnerOptions(): IReadonlyObservableArray<IListBoxItem<IdentityRef>> {
        return this.commentOwnerFilterOptions;
    }

    public getCommentPreferenceOptions(): IReadonlyObservableArray<IListBoxItem<string>> {
        return this.commentPreferenceFilterOptions;
    }

    public getCommentStatusOptions(): IReadonlyObservableArray<IListBoxItem<string>> {
        return this.commentStatusFilterOptions;
    }

    public getFileTypeOptions(): IReadonlyObservableArray<IListBoxItem<string>> {
        return this.fileTypeFilterOptions;
    }

    public getFilteredThreads(): IReadonlyObservableArray<IGitPullRequestCommentThreadModel> {
        return this.filteredThreads;
    }

    public setFilterState(filterState: IFilterState, selectedThreadId?: number): void {
        const commentPreferences = filterState[DiscussionFilterKeys.commentPreference];
        if (commentPreferences) {
            this.commentPreference = (commentPreferences.value || [])[0];
        }

        const commentStatus = filterState[DiscussionFilterKeys.commentStatus];
        if (commentStatus) {
            this.commentStatus = commentStatus.value || [];
        }

        const commentOwner = filterState[DiscussionFilterKeys.commentOwner];
        if (commentOwner) {
            this.commentOwner = (commentOwner.value || [])[0];
        }

        this.selectedThreadId = selectedThreadId;
    }

    public applyFilter(filterState: IFilterState): void {
        this.setFilterState(filterState);
        this.applyFilterOnThreads(this.discussionThreads || []);
    }

    public applyFilterOnThreads(discussionThreads: IGitPullRequestCommentThreadModel[], visible?: boolean, expanded?:boolean): void {
        for (const thread of discussionThreads) {
            thread.setVisible(typeof visible === "boolean" ? visible : this.isDiscussionThreadVisible(thread));
            thread.setExpanded(typeof expanded === "boolean" ? expanded : thread.getVisible().value);
        }

        this.filteredThreads.value = [...discussionThreads];
        this.updateCounts();
    }

    public updateFilePaths(filePaths: string[]): void {
        this.filePaths = filePaths;
        this.updateCounts();
    }

    public updateDiscussionThreads(discussionThreads: IGitPullRequestCommentThreadModel[]): void {
        this.discussionThreads = discussionThreads;

        // Update comment owners using the new set of discussion threads
        this.updateCommentOwners(discussionThreads);

        // Apply initial filter using the provided set of discussion threads if not done already
        if (!this.discussionThreadsFiltered) {
            this.discussionThreadsFiltered = true;
            this.applyFilterOnThreads(discussionThreads);
        } else {
            this.updateCounts();
        }
    }

    private isDiscussionThreadVisible(thread: IGitPullRequestCommentThreadModel): boolean {
        return (
            this.selectedThreadId === thread.id ||
            (this.isVisibleForCommentPreference(thread) && this.isVisibleForCommentStatus(thread) && this.isVisibleForCommentOwner(thread))
        );
    }

    private isVisibleForCommentPreference(thread: IGitPullRequestCommentThreadModel): boolean {
        if (this.commentPreference === DiscussionFilterCommentPreferenceKeys.show) {
            return true;
        } else if (this.commentPreference === DiscussionFilterCommentPreferenceKeys.new) {
            return thread.isUnread().value;
        }

        return false;
    }

    private isVisibleForCommentStatus(thread: IGitPullRequestCommentThreadModel): boolean {
        if (this.commentStatus) {
            if (this.commentStatus.indexOf(DiscussionFilterCommentStatusKeys.all) >= 0) {
                return true;
            }

            const threadStatus = thread.getStatus().value;
            for (const status of this.commentStatus) {
                const statusItem = this.commentStatusMap[status];
                if (statusItem && statusItem.status === threadStatus) {
                    return true;
                }
            }
        }

        return false;
    }

    private isVisibleForCommentOwner(thread: IGitPullRequestCommentThreadModel): boolean {
        if (this.commentOwner) {
            if (this.commentOwner === DiscussionFilterCommentOwnerKeys.all) {
                return true;
            }

            return thread
                .getComments()
                .value.filter(defaultCommentFilter)
                .some(c => c.isOwner(this.commentOwner as IdentityRef));
        }

        return false;
    }

    private updateCommentOwners(discussionThreads: IGitPullRequestCommentThreadModel[]): void {
        const commentOwnersMap: { [id: string]: true } = {};
        const commentOwners: IdentityRef[] = [];
        for (const thread of discussionThreads.filter(defaultThreadFilter)) {
            for (const comment of thread.getComments().value.filter(defaultCommentFilter)) {
                const commentAuthor = comment.getAuthor();
                if (commentAuthor.id !== this.currentUser.id && !commentOwnersMap[commentAuthor.id]) {
                    commentOwnersMap[commentAuthor.id] = true;
                    commentOwners.push(commentAuthor);
                }
            }
        }

        commentOwners.sort((c1, c2) => localeIgnoreCaseComparer(c1.displayName, c2.displayName));

        const newOwnersKey = [...[DiscussionFilterCommentOwnerKeys.all, this.currentUser.id], ...commentOwners.map(co => co.id)].join(",");
        const currentOwnersKey = this.commentOwnerFilterOptions.value.map(c => c.id).join(",");

        if (newOwnersKey !== currentOwnersKey) {
            this.commentOwnerCounts = {};
            for (const co of commentOwners) {
                this.commentOwnerCounts[co.id] = new ObservableValue(0);
            }

            this.commentOwnerFilterOptions.splice(
                2, // skip all + my comments
                this.commentOwnerFilterOptions.length - 2, // Delete owners other than all + my comments
                ...commentOwners.map(co => {
                    return {
                        id: co.id,
                        render: (ri, ci, tc, ti) => renderFilterIdentityRowWithCount(this.commentOwnerCounts[co.id], ri, ci, tc, ti),
                        text: co.displayName,
                        data: co
                    } as IListBoxItem<IdentityRef>;
                })
            );
        }
    }

    private updateCounts(): void {
        const filePathsMap: { [path: string]: boolean } = {};
        if (this.filePaths) {
            for (const f of this.filePaths) {
                filePathsMap[f] = true;
            }

            if (this.discussionThreads && this.commentPreference && this.commentStatus && this.commentOwner) {
                const discussionThreads = this.discussionThreads.filter(
                    t => t.thread.threadContext && t.thread.threadContext.filePath && filePathsMap[t.thread.threadContext.filePath]
                );

                let visibleCount = 0;
                let newCount = 0;
                let hiddenCount = 0;

                const statusCounts: { [status: number]: number } = {};
                Object.keys(this.commentStatusMap).forEach(k => {
                    const statusItem = this.commentStatusMap[k];
                    statusCounts[statusItem.status] = 0;
                });

                const ownerCounts: { [id: string]: number } = {};
                this.commentOwnerFilterOptions.value.forEach(co => {
                    ownerCounts[co.id] = 0;
                });

                for (const thread of discussionThreads) {
                    const isVisibleByPreference = this.isVisibleForCommentPreference(thread);
                    const isVisibleByStatus = this.isVisibleForCommentStatus(thread);
                    const isVisibleByOwner = this.isVisibleForCommentOwner(thread);

                    // Calculate preference counts
                    if (isVisibleByStatus && isVisibleByOwner) {
                        visibleCount += 1;
                        newCount += thread.isUnread().value ? 1 : 0;
                        hiddenCount += 1;
                    }

                    // Calculate status counts
                    if (isVisibleByPreference && isVisibleByOwner) {
                        statusCounts[DiscussionStatus.Unknown] += 1;
                        statusCounts[thread.getStatus().value] += 1;
                    }

                    // Calculate owner counts
                    if (isVisibleByPreference && isVisibleByStatus) {
                        // Even if someone may have multiple comments under a thread, we count it 1
                        const threadCommentMap: { [id: string]: true } = {};
                        for (const comment of thread.getComments().value.filter(defaultCommentFilter)) {
                            const ownerId = comment.getAuthor().id;
                            if (!threadCommentMap[ownerId]) {
                                threadCommentMap[ownerId] = true;
                                ownerCounts[DiscussionFilterCommentOwnerKeys.all] += 1;
                                ownerCounts[ownerId] += 1;
                            }
                        }
                    }
                }

                setCount(this.visibleCommentCount, visibleCount);
                setCount(this.newCommentCount, newCount);
                setCount(this.hiddenCommentCount, hiddenCount);

                Object.keys(this.commentStatusMap).forEach(key => {
                    const statusItem = this.commentStatusMap[key];
                    setCount(statusItem.count, statusCounts[statusItem.status]);
                });

                Object.keys(this.commentOwnerMap).forEach(key => {
                    const ownerItem = this.commentOwnerMap[key];
                    setCount(ownerItem.count, ownerCounts[key]);
                });

                Object.keys(this.commentOwnerCounts).forEach(key => {
                    const ownerCount = this.commentOwnerCounts[key];
                    setCount(ownerCount, ownerCounts[key]);
                });
            }
        }
    }
}

const serviceName = "IPullRequestDiscussionFilterService";
Services.add(serviceName, { serviceFactory: PullRequestDiscussionFilterService });

export function getDiscussionFilterService(pageContext: IVssPageContext): IPullRequestDiscussionFilterService {
    return pageContext.getService<IPullRequestDiscussionFilterService>(serviceName);
}
